import React, { useState } from 'react'
import { CBadge, CButton, CFormInput, CInputGroup, CSmartTable, CSpinner } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilMinus, cilPlus, cilSearch } from '@coreui/icons'
import { toast } from 'react-toastify'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TeamPlayerService from 'src/service/TeamPlayerService'

// The pool is fetched unpaginated, so render it in one page rather than slicing it.
const PAGE_SIZE = 500

// "Player List" panel of the squad builder: the whole player pool, one add/remove per row.
// The list and the squad both come from the parent, which reloads them after every change.
const Table = ({ players, squadIds, loading, fixtureId, teamId, gameType, onChanged }) => {
  const [searchText, setSearchText] = useState('')
  // The row whose add/remove request is still in flight.
  const [pendingId, setPendingId] = useState(null)

  const addPlayer = (playerId) => {
    setPendingId(playerId)
    TeamPlayerService.addInstantPlayer(playerId, fixtureId, teamId)
      .then((res) => {
        if (res.status === 200) {
          toast.dismiss()
          ToastComponent(res.message, 'success')
          onChanged()
        }
      })
      .catch(() => ToastComponent('Unable to add this player.', 'error'))
      .finally(() => setPendingId(null))
  }

  const removePlayer = (playerId) => {
    setPendingId(playerId)
    TeamPlayerService.deleteTeamPlayer(playerId, fixtureId)
      .then((res) => {
        if (res.status === 200) {
          toast.dismiss()
          ToastComponent(res.message, 'success')
          onChanged()
        }
      })
      .catch(() => ToastComponent('Unable to remove this player.', 'error'))
      .finally(() => setPendingId(null))
  }

  // gameType 1 lists the whole club, so the team column tells rows apart. Every other game
  // type is already scoped to one team, which makes the column a column of repeats.
  const showTeamColumn = gameType === 1

  const columns = [
    { label: 'Player Name', key: 'full_name' },
    ...(showTeamColumn ? [{ label: 'Team', key: 'team_name' }] : []),
    { label: 'Status', key: 'squad_status', _style: { width: '20%' }, sorter: false },
    { label: '', key: 'action', _style: { width: '30%' }, sorter: false },
  ]

  const search = searchText.trim().toLowerCase()
  const visiblePlayers = players.filter((player) => {
    if (!search) return true
    return `${player.full_name ?? ''} ${player.team_name ?? ''}`.toLowerCase().includes(search)
  })

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <CInputGroup className="ge-search">
          <span className="ge-search-icon">
            <CIcon icon={cilSearch} />
          </span>
          <CFormInput
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search players..."
            aria-label="Search players by name"
          />
        </CInputGroup>
        <div className="ms-auto text-medium-emphasis small">
          {squadIds.length} of {players.length} players picked
        </div>
      </div>

      <div className="ge-datatable ge-datatable--no-select">
        <CSmartTable
          columns={columns}
          items={visiblePlayers}
          itemsPerPage={PAGE_SIZE}
          loading={loading}
          pagination={false}
          noItemsLabel={
            search ? 'No players match your search.' : 'No players found for this team.'
          }
          columnSorter={{ resetable: false }}
          scopedColumns={{
            full_name: (item) => <td className="fw-semibold">{item.full_name ?? '-'}</td>,
            team_name: (item) => <td>{item.team_name ?? '-'}</td>,
            squad_status: (item) =>
              squadIds.includes(item.id) ? (
                <td>
                  <CBadge color="success" shape="rounded-pill" className="px-2 py-1">
                    In Squad
                  </CBadge>
                </td>
              ) : (
                <td>
                  <CBadge color="secondary" shape="rounded-pill" className="px-2 py-1">
                    Available
                  </CBadge>
                </td>
              ),
            action: (item) => {
              const inSquad = squadIds.includes(item.id)
              const pending = pendingId === item.id
              return (
                <td className="text-end">
                  <CButton
                    size="sm"
                    color={inSquad ? 'danger' : 'success'}
                    variant="outline"
                    disabled={pending}
                    title={inSquad ? 'Remove from squad' : 'Add to squad'}
                    onClick={() => (inSquad ? removePlayer(item.id) : addPlayer(item.id))}
                  >
                    {pending ? (
                      <CSpinner size="sm" />
                    ) : (
                      <>
                        <CIcon icon={inSquad ? cilMinus : cilPlus} className="me-1" />
                        {inSquad ? 'Remove' : 'Add'}
                      </>
                    )}
                  </CButton>
                </td>
              )
            },
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
        />
      </div>
    </>
  )
}

export default Table
